import React from "react";
import {
  alpha,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatShortDate } from "../utils/planSelectors";

function GoalSwitch({ options = [], selectedGoalId, onSelect }) {
  if (options.length <= 1) return null;
  return (
    <Box sx={{ mt: 2, maxWidth: 420 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
        Switch focus
      </Typography>
      <Select
        size="small"
        fullWidth
        value={selectedGoalId || options[0]?.id || ""}
        onChange={(event) => onSelect(event.target.value)}
        sx={{
          borderRadius: "999px",
          bgcolor: "#fff",
          fontWeight: 800,
          "& .MuiSelect-select": { py: 1, px: 1.5 },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

export default function FocusCard({
  goal,
  goalOptions,
  selectedGoalId,
  onSelectGoal,
  dueDate,
  daysRemaining,
  hasNumericTarget = true,
  onNewGoal,
  onEditGoal,
  onUpdateProgress,
  onOpenDetails,
}) {
  if (!goal) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          p: { xs: 3, md: 4 },
          minHeight: 190,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0, textTransform: "uppercase", color: "primary.main", mb: 1 }}>
            Goal
          </Typography>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Choose your first focus
          </Typography>
          <Typography sx={{ color: "text.secondary", maxWidth: 460 }}>
            Start with a goal, then add the habits TrustBud should help you keep.
          </Typography>
        </Box>
        <Button variant="contained" onClick={onNewGoal}>
          Add goal
        </Button>
      </Paper>
    );
  }

  const progressValue = Math.max(0, Math.min(100, Number(goal.progress || 0)));

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        p: { xs: 3, md: 4 },
        minHeight: 190,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.8fr) minmax(240px, 0.8fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#17345d", 0.08),
              color: "primary.main",
              mb: 2,
            }}
          >
            <TrackChangesOutlinedIcon fontSize="small" />
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0, textTransform: "uppercase", color: "primary.main", mb: 1 }}>
            Current goal
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 28, md: 32 },
              lineHeight: 1.08,
              fontWeight: 800,
              color: "text.primary",
              mb: 2.5,
              maxWidth: 620,
            }}
          >
            {goal.title}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontWeight: 700, mb: 2 }}>
            {(goal.commitments || []).filter((item) => item?.is_active !== false).length} active habit{(goal.commitments || []).filter((item) => item?.is_active !== false).length === 1 ? "" : "s"}
          </Typography>
          <Box sx={{ maxWidth: 560 }}>
            {hasNumericTarget ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.secondary" }}>
                    Progress
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.main" }}>
                    {progressValue}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha("#17345d", 0.09),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      backgroundColor: "primary.main",
                    },
                  }}
                />
                {onUpdateProgress ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onUpdateProgress}
                    sx={{ mt: 1.4, borderRadius: "12px", fontWeight: 800 }}
                  >
                    Update progress
                  </Button>
                ) : null}
              </>
            ) : (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha("#17345d", 0.035),
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "text.primary" }}>
                  How TrustBud tracks this
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: 13.5, color: "text.secondary" }}>
                  Not every goal needs a number. TrustBud watches the habits behind it and whether the plan stays followable.
                </Typography>
              </Box>
            )}
          </Box>
          <GoalSwitch
            options={goalOptions}
            selectedGoalId={selectedGoalId}
            onSelect={onSelectGoal}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "8px",
            p: 2.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: alpha("#17345d", 0.03),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FlagOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary" }}>
                Target date
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              {onOpenDetails ? (
                <Button
                  size="small"
                  startIcon={<InfoOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={onOpenDetails}
                  sx={{ minWidth: 0, px: 0.75 }}
                >
                  Details
                </Button>
              ) : null}
              <Button
                size="small"
                startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={onEditGoal}
                sx={{ minWidth: 0, px: 0.75 }}
              >
                Edit
              </Button>
            </Stack>
          </Box>
          {dueDate ? (
            <>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: "text.primary", mb: 0.75 }}>
                {formatShortDate(dueDate)}
              </Typography>
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                {daysRemaining === null
                  ? "No target date yet"
                  : daysRemaining < 0
                    ? `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? "" : "s"} overdue`
                    : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`}
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary", mb: 0.75 }}>
                No target date yet
              </Typography>
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                Add one so this goal has a clear finish line.
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Paper>
  );
}
